import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Svg,
  Polygon,
} from "@react-pdf/renderer";
import { PreconFormValues } from "Pages/Admin/PreconAuthoring/types/precon.types";

const DEFAULT_COMPANY_INFO = {
  name: "CSC New England",
  legalName: "Consultant Services New England, LLC",
  address: "23 Lookout Drive, Freeport, ME 04032",
  phone: "(207) 650-7435",
  email: "info@cscnewengland.com",
};

export interface AdminUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  companyName?: string;
}

export interface PreconPdfProps {
  values: PreconFormValues;
  adminUser: AdminUser;
}

const C = {
  navy: "#14508c",
  navyLight: "#e8f0f9",
  ink: "#1a1a2e",
  body: "#2d2d3a",
  muted: "#6b7280",
  line: "#dde1e6",
  green: "#16a34a",
  amber: "#b45309",
  white: "#ffffff",
  rowAlt: "#f8fafc",
};

const s = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 8,
    color: C.body,
    paddingTop: 42,
    paddingBottom: 36,
    paddingHorizontal: 40,
    lineHeight: 1.45,
  },
  // ── Cover header ─────────────────────────────────────────────────────────────
  coverStrip: {
    backgroundColor: C.navy,
    borderRadius: 6,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  coverLabel: {
    fontFamily: "Helvetica",
    fontSize: 7,
    color: "rgba(255,255,255,0.7)",
    letterSpacing: 1.2,
    marginBottom: 4,
  },
  coverTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 15,
    color: C.white,
    marginBottom: 5,
    lineHeight: 1.1,
  },
  coverMeta: {
    fontFamily: "Helvetica",
    fontSize: 7.5,
    color: "rgba(255,255,255,0.8)",
    marginTop: 4,
  },
  // ── Section ──────────────────────────────────────────────────────────────────
  section: {
    marginBottom: 10,
    marginTop: 10,
  },
  sectionTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7,
    color: C.navy,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    borderBottomWidth: 1,
    borderBottomColor: C.navyLight,
    paddingBottom: 3,
    marginBottom: 6,
  },
  bodyText: {
    fontSize: 8,
    color: C.body,
    lineHeight: 1.5,
  },
  // ── Bullet list ──────────────────────────────────────────────────────────────
  bulletRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  bullet: {
    fontSize: 8,
    color: C.muted,
    width: 10,
    marginTop: 0.5,
  },
  bulletText: {
    flex: 1,
    fontSize: 8,
    color: C.body,
    lineHeight: 1.45,
  },
  // ── Table ────────────────────────────────────────────────────────────────────
  table: {
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: C.navyLight,
    borderBottomWidth: 1,
    borderBottomColor: C.line,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 3.5,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.line,
  },
  tableRowAlt: {
    backgroundColor: C.rowAlt,
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  tableTotalRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderTopWidth: 1,
    borderTopColor: C.navy,
    backgroundColor: C.navyLight,
  },
  th: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7,
    color: C.navy,
  },
  td: {
    fontSize: 7.5,
    color: C.body,
  },
  tdBold: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7.5,
    color: C.body,
  },
  // action table status cells
  statusComplete: { fontSize: 7.5, color: C.green },
  statusNA: { fontSize: 7.5, color: C.muted },
  statusPending: { fontSize: 7.5, color: C.amber },
  // ── Signature ────────────────────────────────────────────────────────────────
  sigBox: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    borderTopWidth: 1,
    borderTopColor: C.line,
    paddingTop: 8,
    marginTop: 6,
  },
  sigName: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8.5,
    color: C.ink,
  },
  sigMeta: { fontSize: 7.5, color: C.muted, marginTop: 2 },
  sigScript: {
    fontFamily: "Helvetica-Oblique",
    fontSize: 13,
    color: C.navy,
    textAlign: "right",
  },
  sigDate: { fontSize: 7, color: C.muted, textAlign: "right", marginTop: 4 },
  // ── Footer ───────────────────────────────────────────────────────────────────
  footer: {
    position: "absolute",
    bottom: 16,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: C.line,
    paddingTop: 4,
  },
  footerText: { fontSize: 6.5, color: C.muted },

  // ── Cover page (Pre-Construction Report) ────────────────────────────
  // Light-blue accent theme — mirrors the reference mockup's default
  // (non-"progress") cover variant, unlike the Construction Progress
  // Report cover, which uses the gold accent variant.
  coverPage: {
    fontFamily: "Helvetica",
    backgroundColor: "#fdfdfb",
    color: "#111111",
  },
  coverHeaderBand: {
    height: 306, // 4.25in
    backgroundColor: "#0d3a66",
    paddingTop: 43,
    paddingHorizontal: 54,
    paddingBottom: 20,
    position: "relative",
  },
  coverHeaderAccent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: "#7BBEF0",
  },
  coverLogoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 26,
  },
  coverLogoMark: { flexDirection: "row", alignItems: "center", gap: 10 },
  coverPeaksRow: { flexDirection: "row", gap: 4 },
  coverLogoCsc: {
    fontFamily: "Times-Roman",
    fontSize: 22,
    color: "#ffffff",
    letterSpacing: 1,
  },
  coverLogoNe: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    color: "#ffffff",
    letterSpacing: 2,
    marginTop: 2,
  },
  coverLogoTag: {
    fontSize: 7,
    color: "#a8c8ea",
    fontFamily: "Helvetica-Oblique",
    marginTop: 2,
  },
  coverBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#7BBEF080",
    borderRadius: 12,
    paddingVertical: 5,
    paddingHorizontal: 12,
  },
  coverBadgeDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: "#7BBEF0",
  },
  coverBadgeText: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7.5,
    color: "#7BBEF0",
    letterSpacing: 1.2,
  },
  coverTitleEyebrowRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  coverTitleEyebrowLine: {
    width: 26,
    height: 1.5,
    backgroundColor: "#7BBEF0",
  },
  coverTitleEyebrow: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#7BBEF0",
    letterSpacing: 1.6,
  },
  coverReportTitle: {
    fontFamily: "Times-Bold",
    fontSize: 30,
    color: "#ffffff",
    lineHeight: 1.15,
    marginBottom: 8,
  },
  coverReportSub: {
    fontFamily: "Helvetica-Oblique",
    fontSize: 9.5,
    color: "#a8c8ea",
    lineHeight: 1.5,
    maxWidth: 380,
  },
  coverContent: {
    paddingTop: 26,
    paddingHorizontal: 54,
    paddingBottom: 20,
    flexGrow: 1,
  },
  coverProjectEyebrow: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8.5,
    color: "#14508c",
    letterSpacing: 1.4,
    marginBottom: 6,
  },
  coverProjectName: {
    fontFamily: "Times-Roman",
    fontSize: 22,
    color: "#111111",
    lineHeight: 1.15,
    marginBottom: 5,
  },
  coverProjectAddress: { fontSize: 10, color: "#555c66" },
  coverProjectDivider: {
    height: 2,
    backgroundColor: "#14508c",
    width: "50%",
    marginTop: 12,
    marginBottom: 20,
  },
  coverDetailsGrid: { marginBottom: 6 },
  coverDetailsRow: { flexDirection: "row", marginBottom: 14 },
  coverDetail: {
    flex: 1,
    borderLeftWidth: 1.5,
    borderLeftColor: "#14508c",
    paddingLeft: 10,
    paddingRight: 10,
  },
  coverDetailLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7,
    color: "#909399",
    letterSpacing: 1,
    marginBottom: 3,
  },
  coverDetailValue: {
    fontFamily: "Times-Roman",
    fontSize: 11,
    color: "#111111",
  },
  coverDetailSub: { fontSize: 7.5, color: "#909399", marginTop: 2 },
  coverCertBlock: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    backgroundColor: "#fef8ec",
    borderWidth: 1,
    borderColor: "#e8c471",
    borderRadius: 6,
    padding: 14,
    marginTop: 6,
  },
  coverCertIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: "#c8983a",
    color: "#ffffff",
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    paddingTop: 6,
  },
  coverCertTitle: {
    fontFamily: "Times-Roman",
    fontSize: 11,
    color: "#7a5c00",
    marginBottom: 4,
  },
  coverCertText: {
    fontSize: 8.5,
    color: "#5a4600",
    lineHeight: 1.5,
  },
  coverFooter: {
    backgroundColor: "#0d3a66",
    paddingVertical: 14,
    paddingHorizontal: 54,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  coverFooterAccent: { height: 3, backgroundColor: "#7BBEF0" },
  coverFooterLeft: {
    fontSize: 7.5,
    color: "#ffffffe6",
    lineHeight: 1.5,
    maxWidth: 340,
  },
  coverFooterRight: {
    fontSize: 7,
    color: "#a8c8ea",
    letterSpacing: 0.6,
    lineHeight: 1.5,
    textAlign: "right",
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

const splitLines = (text: string) =>
  text ? text.split("\n").filter((l) => l.trim()) : [];

const getDeliveryMethodLabel = (value?: string) => {
  switch (value) {
    case "email_platform":
      return "Email + In-platform notification";
    case "platform":
      return "In-platform only";
    case "email":
      return "Email only";
    default:
      return value || "—";
  }
};

const getLockAfterPublishLabel = (value?: string) => {
  switch (value) {
    case "yes":
      return "Yes — lock for editing after publish";
    case "no":
      return "No — allow revisions";
    default:
      return value || "—";
  }
};

const BulletItem = ({ text }: { text: string }) => (
  <View style={s.bulletRow} wrap={false}>
    <Text style={s.bullet}>•</Text>
    <Text style={s.bulletText}>{text}</Text>
  </View>
);

const SectionTitle = ({ children }: { children: string }) => (
  <Text style={s.sectionTitle}>{children}</Text>
);

// Mirrors the cover reference's three-peak logo mark.
const LogoPeaks = () => (
  <View style={s.coverPeaksRow}>
    {[0, 1, 2].map((i) => (
      <Svg key={i} width={20} height={24}>
        <Polygon points="0,24 10,0 20,24" fill="#ffffff" />
      </Svg>
    ))}
  </View>
);

// ── Budget table ─────────────────────────────────────────────────────────────

const COL_BUDGET = {
  scope: "30%",
  val: "14%",
  sf: "14%",
  psf: "14%",
  low: "14%",
  high: "14%",
};

const parseNum = (val: string | undefined) =>
  parseFloat((val ?? "").replace(/[$,]/g, "")) || 0;

const BudgetTable = ({ rows }: { rows: PreconFormValues["budgetRows"] }) => {
  const dataRows = rows.filter((r) => !r.isTotal);
  const totals = {
    value: dataRows.reduce((sum, r) => sum + parseNum(r.value), 0),
    projectSf: dataRows.reduce((sum, r) => sum + parseNum(r.projectSf), 0),
    projectPsf: dataRows.reduce((sum, r) => sum + parseNum(r.projectPsf), 0),
    industryLow: dataRows.reduce((sum, r) => sum + parseNum(r.industryLow), 0),
    industryHigh: dataRows.reduce(
      (sum, r) => sum + parseNum(r.industryHigh),
      0,
    ),
  };

  return (
    <View style={s.table}>
      <View style={s.tableHeader}>
        <Text style={[s.th, { width: COL_BUDGET.scope }]}>Scope</Text>
        <Text style={[s.th, { width: COL_BUDGET.val }]}>Scheduled Value</Text>
        <Text style={[s.th, { width: COL_BUDGET.sf }]}>Project SF</Text>
        <Text style={[s.th, { width: COL_BUDGET.psf }]}>Project $/SF</Text>
        <Text style={[s.th, { width: COL_BUDGET.low }]}>Industry Low</Text>
        <Text style={[s.th, { width: COL_BUDGET.high }]}>Industry High</Text>
      </View>
      {rows.map((row, i) => {
        const isLast = i === rows.length - 1;
        const isTotal = row.isTotal;
        const rowStyle = isTotal
          ? s.tableTotalRow
          : [
              s.tableRow,
              ...(i % 2 !== 0 ? [s.tableRowAlt] : []),
              ...(isLast ? [s.tableRowLast] : []),
            ];
        return (
          <View key={row.id} style={rowStyle} wrap={false}>
            <Text
              style={[isTotal ? s.tdBold : s.td, { width: COL_BUDGET.scope }]}
            >
              {row.trade || "—"}
            </Text>
            <Text
              style={[isTotal ? s.tdBold : s.td, { width: COL_BUDGET.val }]}
            >
              {isTotal ? String(totals.value) : row.value || "—"}
            </Text>
            <Text style={[s.td, { width: COL_BUDGET.sf }]}>
              {isTotal ? String(totals.projectSf) : row.projectSf || "—"}
            </Text>
            <Text style={[s.td, { width: COL_BUDGET.psf }]}>
              {isTotal ? totals.projectPsf.toFixed(2) : row.projectPsf || "—"}
            </Text>
            <Text style={[s.td, { width: COL_BUDGET.low }]}>
              {isTotal ? totals.industryLow.toFixed(2) : row.industryLow || "—"}
            </Text>
            <Text style={[s.td, { width: COL_BUDGET.high }]}>
              {isTotal
                ? totals.industryHigh.toFixed(2)
                : row.industryHigh || "—"}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

// ── Action items table ────────────────────────────────────────────────────────

const ActionTable = ({ items }: { items: PreconFormValues["actionItems"] }) => (
  <View style={s.table}>
    <View style={s.tableHeader}>
      <Text style={[s.th, { width: "18%" }]}>Party</Text>
      <Text style={[s.th, { flex: 1 }]}>Action Item</Text>
      <Text style={[s.th, { width: "14%", textAlign: "right" }]}>Status</Text>
    </View>
    {items.map((item, i) => {
      const isLast = i === items.length - 1;
      const statusStyle =
        item.status === "Complete"
          ? s.statusComplete
          : item.status === "N/A"
            ? s.statusNA
            : s.statusPending;
      return (
        <View
          key={item.id}
          style={[
            s.tableRow,
            ...(i % 2 !== 0 ? [s.tableRowAlt] : []),
            ...(isLast ? [s.tableRowLast] : []),
          ]}
          wrap={false}
        >
          <Text style={[s.td, { width: "18%" }]}>{item.party || "—"}</Text>
          <Text style={[s.td, { flex: 1 }]}>{item.action || "—"}</Text>
          <Text style={[statusStyle, { width: "14%", textAlign: "right" }]}>
            {item.status}
          </Text>
        </View>
      );
    })}
  </View>
);

// ── Document ─────────────────────────────────────────────────────────────────

const PreconPdf = ({ values, adminUser }: PreconPdfProps) => {
  const adminName =
    [adminUser.firstName, adminUser.lastName].filter(Boolean).join(" ") || "—";
  const adminCompany = adminUser.companyName || "";
  const adminContact =
    [adminUser.phone, adminUser.email].filter(Boolean).join(" · ") || "—";

  const getYesNo = (id: string) =>
    values.yesNoItems.find((y) => y.id === id)?.value ?? "no";
  const yesLabel = (id: string, yes: string, no: string) =>
    getYesNo(id) === "yes" ? yes : no;

  const today = new Date().toISOString().slice(0, 10);

  // ── Cover page derived values ────────────────────────────────────
  const copyrightYear = new Date().getFullYear();
  const invoiceRef = values.invoiceNumber
    ? values.invoiceNumber.startsWith("#")
      ? values.invoiceNumber
      : `#${values.invoiceNumber}`
    : "—";
  const contractSumSub = [
    values.contractType,
    values.retainage ? `${values.retainage}% retainage` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const coverDetails: { label: string; value: string; sub?: string }[] = [
    {
      label: "Prepared For",
      value: values.bank || "—",
      sub: values.lendingOfficer || undefined,
    },
    {
      label: "Prepared By",
      value: adminName,
      sub: adminCompany || undefined,
    },
    {
      label: "Report Reference",
      value: invoiceRef,
      sub: values.reportDate ? `Issued ${values.reportDate}` : undefined,
    },
    {
      label: "Contract Sum",
      value: values.totalBudget || "—",
      sub: contractSumSub || undefined,
    },
    { label: "General Contractor", value: values.gcName || "—" },
    { label: "Owner / Borrower", value: values.ownerName || "—" },
  ];
  const coverDetailRows = [
    coverDetails.slice(0, 2),
    coverDetails.slice(2, 4),
    coverDetails.slice(4, 6),
  ];
  const certNarrative =
    values.budgetConclusion ||
    values.closingNarrative ||
    "See the full report below for CSC's complete findings and recommendation.";

  return (
    <Document>
      {/* ══ COVER PAGE — US Letter 8.5×11 ══ */}
      <Page size="A4" style={s.coverPage}>
        <View style={s.coverHeaderBand}>
          <View style={s.coverLogoRow}>
            <View style={s.coverLogoMark}>
              <LogoPeaks />
              <View>
                <Text style={s.coverLogoCsc}>CSC</Text>
                <Text style={s.coverLogoNe}>NEW ENGLAND</Text>
                <Text style={s.coverLogoTag}>
                  Lender Construction &amp; Project Consulting
                </Text>
              </View>
            </View>
            <View style={s.coverBadge}>
              <View style={s.coverBadgeDot} />
              <Text style={s.coverBadgeText}>CONFIDENTIAL REPORT</Text>
            </View>
          </View>

          <View style={s.coverTitleEyebrowRow}>
            <View style={s.coverTitleEyebrowLine} />
            <Text style={s.coverTitleEyebrow}>
              PREPARED FOR LENDER APPROVAL
            </Text>
          </View>
          <Text style={s.coverReportTitle}>{"Pre-Construction\nReview"}</Text>
          <Text style={s.coverReportSub}>
            Comprehensive analysis of project readiness including budget,
            schedule, contract delivery, allowances, contingencies, and regional
            cost benchmarking prior to first draw authorization.
          </Text>

          <View style={s.coverHeaderAccent} />
        </View>

        <View style={s.coverContent}>
          <View>
            <Text style={s.coverProjectEyebrow}>PROJECT</Text>
            <Text style={s.coverProjectName}>{values.projectName || "—"}</Text>
            <Text style={s.coverProjectAddress}>
              {values.projectAddress || "—"}
            </Text>
            <View style={s.coverProjectDivider} />
          </View>

          <View style={s.coverDetailsGrid}>
            {coverDetailRows.map((row, i) => (
              <View key={i} style={s.coverDetailsRow} wrap={false}>
                {row.map((detail) => (
                  <View key={detail.label} style={s.coverDetail}>
                    <Text style={s.coverDetailLabel}>
                      {detail.label.toUpperCase()}
                    </Text>
                    <Text style={s.coverDetailValue}>{detail.value}</Text>
                    {detail.sub ? (
                      <Text style={s.coverDetailSub}>{detail.sub}</Text>
                    ) : null}
                  </View>
                ))}
              </View>
            ))}
          </View>

          <View style={s.coverCertBlock} wrap={false}>
            <Text style={s.coverCertIcon}>✓</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.coverCertTitle}>CSC Recommendation</Text>
              <Text style={s.coverCertText}>{certNarrative}</Text>
            </View>
          </View>
        </View>
      </Page>

      {/* ══ EXISTING REPORT CONTENT (unchanged) ══ */}
      <Page size="A4" style={s.page}>
        {/* ── Cover header ─────────────────────────────────────────────── */}
        <View style={s.coverStrip}>
          <Text style={s.coverLabel}>PRE-CONSTRUCTION REVIEW</Text>
          <Text style={s.coverTitle}>
            {values.projectName || "—"}
            {values.projectAddress ? ` — ${values.projectAddress}` : ""}
          </Text>
          <Text style={s.coverMeta}>
            {[
              values.reportDate,
              values.bank,
              values.lendingOfficer,
              values.invoiceNumber
                ? `Invoice ${values.invoiceNumber.startsWith("#") ? values.invoiceNumber : `#${values.invoiceNumber}`}`
                : "",
            ]
              .filter(Boolean)
              .join("  ·  ")}
          </Text>
        </View>

        {/* ── Invoice & Project Overview ────────────────────────────────── */}
        <View style={s.section}>
          <SectionTitle>Invoice &amp; Project Overview</SectionTitle>
          <Text style={s.bodyText}>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>To:</Text>{" "}
            {values.bank || "—"} · Attn: {values.lendingOfficer || "—"}
          </Text>
          <Text style={[s.bodyText, { marginTop: 2 }]}>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>Project:</Text>{" "}
            {values.projectName || "—"} · {values.projectAddress || "—"}
          </Text>
          <Text style={[s.bodyText, { marginTop: 2 }]}>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>For:</Text>{" "}
            {values.servicesRequested || "Preconstruction Report"} ·
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {" "}
              Total Due: {values.invoiceAmount || "—"} · Invoice{" "}
              {values.invoiceNumber
                ? values.invoiceNumber.startsWith("#")
                  ? values.invoiceNumber
                  : `#${values.invoiceNumber}`
                : "—"}
            </Text>
          </Text>
          <Text style={[s.bodyText, { marginTop: 2 }]}>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>Owner:</Text>{" "}
            {values.ownerName || "—"} ·
            <Text style={{ fontFamily: "Helvetica-Bold" }}> Contractor:</Text>{" "}
            {values.gcName || "—"} ·
            <Text style={{ fontFamily: "Helvetica-Bold" }}> Services:</Text>{" "}
            {values.servicesRequested || "—"}
          </Text>
        </View>

        {/* ── Certification ────────────────────────────────────────────── */}
        {values.certificationOpening ? (
          <View style={s.section}>
            <SectionTitle>Certification</SectionTitle>
            <Text style={s.bodyText}>{values.certificationOpening}</Text>
          </View>
        ) : null}

        {/* ── Executive Summary ─────────────────────────────────────────── */}
        <View style={s.section}>
          <SectionTitle>Project Description / Executive Summary</SectionTitle>
          {splitLines(values.primaryDescriptionPoints).map((line, i) => (
            <BulletItem key={i} text={line} />
          ))}
          {values.totalSf ? (
            <BulletItem
              text={`Approximate total square footage: ${values.totalSf}`}
            />
          ) : null}
          {values.totalBudget || values.hardCostBudget ? (
            <BulletItem
              text={[
                values.totalBudget ? `Total budget: ${values.totalBudget}` : "",
                values.hardCostBudget
                  ? `Hard cost budget: ${values.hardCostBudget}${values.costPerSf ? ` (~${values.costPerSf}/SF)` : ""}`
                  : "",
              ]
                .filter(Boolean)
                .join("  ·  ")}
            />
          ) : null}
          {values.mobilizationDate || values.estCompletion ? (
            <BulletItem
              text={`Mobilization: ${values.mobilizationDate || "—"}  ·  Est. completion: ${values.estCompletion || "—"}${values.statedDuration ? ` (${values.statedDuration})` : ""}`}
            />
          ) : null}
          {splitLines(values.additionalDescription).map((line, i) => (
            <BulletItem key={`ad-${i}`} text={line} />
          ))}
        </View>

        {/* ── Contract Delivery ─────────────────────────────────────────── */}
        <View style={s.section}>
          <SectionTitle>Contract Delivery Method &amp; Review</SectionTitle>
          {values.gcName ? (
            <BulletItem
              text={`${values.gcName} — ${values.contractType || "contract type not specified"} with budget of ${values.totalBudget || "—"}`}
            />
          ) : null}
          <BulletItem
            text={[
              values.contractorFee
                ? `Contractor Fee: ${values.contractorFee}`
                : "No Contractor Fee carried",
              values.contingencyDisplay
                ? `Contingency: ${values.contingencyDisplay}`
                : "",
              values.contingencyPct
                ? `(${values.contingencyPct} of hard costs)`
                : "",
            ]
              .filter(Boolean)
              .join("  ·  ")}
          />
          <BulletItem
            text={yesLabel(
              "permits",
              "Permits by Owner/GC — building permits requested prior to first disbursement",
              "Permits — not confirmed",
            )}
          />
          <BulletItem
            text={yesLabel(
              "buildersRisk",
              "Builder's Risk and Liability/Workers Comp Insurance by Owner/GC — certificates attached",
              "Builder's Risk Insurance — not confirmed",
            )}
          />
          <BulletItem
            text={[
              yesLabel(
                "bond",
                "Performance and Payment Bond required",
                "No Performance and Payment Bond",
              ),
              yesLabel(
                "liquidated",
                "Liquidated damages apply",
                "No liquidated damages",
              ),
              yesLabel(
                "bonus",
                "Early completion bonus applies",
                "No early completion bonus",
              ),
              yesLabel(
                "savings",
                "Shared savings clause included",
                "No shared savings",
              ),
            ].join("  ·  ")}
          />
          {values.retainage ? (
            <BulletItem
              text={`${values.retainage}% retainage stated — 5–10% is typical. Lender should verify retainage requirements with Lending Agreement.`}
            />
          ) : null}
          {splitLines(values.contractNarrative).map((line, i) => (
            <BulletItem key={`cn-${i}`} text={line} />
          ))}
        </View>

        {/* ── Allowances & Contingencies ────────────────────────────────── */}
        <View style={s.section}>
          <SectionTitle>Allowances &amp; Contingencies</SectionTitle>
          {splitLines(values.allowancesNarrative).length > 0 ? (
            splitLines(values.allowancesNarrative).map((line, i) => (
              <BulletItem key={i} text={line} />
            ))
          ) : (
            <BulletItem text="Allowances: None listed" />
          )}
          {values.gcContingencyAmount ? (
            <BulletItem
              text={[
                `Contingency: ${values.gcContingencyAmount}`,
                values.contingencyPct
                  ? `(${values.contingencyPct} of hard costs)`
                  : "",
                values.industryRange
                  ? `— industry typical range: ${values.industryRange}`
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          ) : null}
          {splitLines(values.contingencyNarrative).map((line, i) => (
            <BulletItem key={`contn-${i}`} text={line} />
          ))}
          {splitLines(values.clarificationsNarrative).map((line, i) => (
            <BulletItem key={`clar-${i}`} text={line} />
          ))}
        </View>

        {/* ── Budget Analysis ───────────────────────────────────────────── */}
        <View style={s.section}>
          <SectionTitle>
            Construction Budget Analysis — Major Trade Comparison
          </SectionTitle>
          <BudgetTable rows={values.budgetRows} />
          {values.budgetNarrative ? (
            <Text style={[s.bodyText, { marginTop: 4 }]}>
              {values.budgetNarrative}
            </Text>
          ) : null}
          {values.budgetConclusion ? (
            <Text
              style={[
                s.bodyText,
                { marginTop: 2, fontFamily: "Helvetica-Bold" },
              ]}
            >
              {values.budgetConclusion}
            </Text>
          ) : null}
        </View>

        {/* ── Schedule Analysis ─────────────────────────────────────────── */}
        <View style={s.section}>
          <SectionTitle>Schedule Analysis</SectionTitle>
          <BulletItem
            text={
              values.masterScheduleProvided || "No Master Schedule provided"
            }
          />
          {values.mobilizationDate || values.estCompletion ? (
            <BulletItem
              text={[
                values.statedDuration
                  ? `${values.statedDuration} timeframe:`
                  : "",
                values.mobilizationDate
                  ? `mobilization ${values.mobilizationDate}`
                  : "",
                values.estCompletion
                  ? `substantial completion ${values.estCompletion}`
                  : "",
              ]
                .filter(Boolean)
                .join(", ")}
            />
          ) : null}
          {values.typicalDuration ? (
            <BulletItem
              text={`Projects of this nature typically take ${values.typicalDuration}`}
            />
          ) : null}
          {splitLines(values.scheduleNarrative).map((line, i) => (
            <BulletItem key={`sn-${i}`} text={line} />
          ))}
        </View>

        {/* ── Closing Comments ──────────────────────────────────────────── */}
        <View style={s.section}>
          <SectionTitle>Closing Comments / Summary</SectionTitle>
          {splitLines(values.closingNarrative).length > 0 ? (
            splitLines(values.closingNarrative).map((line, i) => (
              <BulletItem key={i} text={line} />
            ))
          ) : (
            <BulletItem text="No closing comments entered." />
          )}
        </View>

        {/* ── Action Items ──────────────────────────────────────────────── */}
        {values.actionItems.length > 0 ? (
          <View style={s.section}>
            <SectionTitle>Action Items</SectionTitle>
            <ActionTable items={values.actionItems} />
          </View>
        ) : null}

        {/* ── Signature ─────────────────────────────────────────────────── */}
        <View style={s.sigBox} wrap={false}>
          <View>
            <Text style={s.sigName}>
              {adminName}
              {adminCompany ? `  ·  ${adminCompany}` : ""}
            </Text>
            <Text style={s.sigMeta}>{adminContact}</Text>
          </View>
          <View>
            <Text style={s.sigScript}>{adminName}</Text>
            <Text style={s.sigDate}>{values.reviewDate || today}</Text>
          </View>
        </View>
        {/* ── Delivery Settings ────────────────────────────────────────── */}
        <View style={s.section}>
          <SectionTitle>Delivery Settings</SectionTitle>
          <BulletItem text={`Deliver To: ${values.deliverTo || "—"}`} />
          <BulletItem
            text={`CC: ${values.deliveryCC.length ? values.deliveryCC.join(", ") : "—"}`}
          />
          <BulletItem
            text={`Delivery Method: ${getDeliveryMethodLabel(values.deliveryMethod)}`}
          />
          <BulletItem
            text={`Lock After Publishing: ${getLockAfterPublishLabel(values.lockAfterPublish)}`}
          />
        </View>

        {/* Closing company footer — same block as the cover page, shown once
            at the end of the report rather than repeated on every page.
            marginHorizontal cancels out s.page's paddingHorizontal (40) so
            this block reaches the true page edges like it does on the cover. */}
        <View style={{ marginTop: 18, marginHorizontal: -40 }} wrap={false}>
          <View style={s.coverFooterAccent} />
          <View style={s.coverFooter}>
            <Text style={s.coverFooterLeft}>
              <Text style={{ fontFamily: "Helvetica-Bold", color: "#ffffff" }}>
                {DEFAULT_COMPANY_INFO.name}
              </Text>{" "}
              · {DEFAULT_COMPANY_INFO.legalName}
              {"\n"}
              {DEFAULT_COMPANY_INFO.address} · {DEFAULT_COMPANY_INFO.phone} ·{" "}
              {DEFAULT_COMPANY_INFO.email}
            </Text>
            <Text style={s.coverFooterRight}>
              {"Confidential\n"}© {copyrightYear} · All rights reserved
            </Text>
          </View>
        </View>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            Pre-Construction Report · {values.projectName || "—"} · CSC New
            England
          </Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default PreconPdf;
